import { useNavigate, useParams, Link } from "react-router-dom";
import PageList from "../../templates/PageList";
import PageSearch from "../../molecules/PageSearch";
import { FaPlus, FaChevronDown, FaSync, FaLink, FaUnlink, FaLayerGroup, FaFileExport, FaFileSignature, FaHandHoldingUsd } from "react-icons/fa";
import { useState, useMemo } from "react";
import BadgeStatus from "../../atoms/BadgeStatus";
import { getStatusColor } from "./utils/contractStatus";
import Tooltip from "../../atoms/Tooltip";
import appConfig from "../../../config/app-config";

const OperationsPage = () => {
    const navigate = useNavigate();
    const { page = '0', pageSize = '100', field: fieldSorted = 'id', order: orderSorted = 'ASC', filter = 's=;' }: any = useParams();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [billingInfoModal, setBillingInfoModal] = useState<string | null>(null);

    let searchFilter = '';

    if (filter) {
        [searchFilter = ''] = filter.match(/=(.*?);/g)
            ?.map((match: string) => match.slice(1, -1)) || [''];
    }

    const { address: CONTRACTS_API_URL, key: CONTRACTS_API_KEY } = appConfig.apis.contractsManagementApiEnv;

    const url = `${CONTRACTS_API_URL}/contracts/receivables_assignment/list?search=${searchFilter}&page=${page}&pageSize=${pageSize}&sortField=${fieldSorted}&sortOrder=${orderSorted}&refresh=${refreshKey}`;

    const fetchHeaders = useMemo(() =>
        [{ key: 'x-api-key', value: CONTRACTS_API_KEY || '' }],
        [CONTRACTS_API_KEY]
    );

    const formatOperationType = (type: string) => {
        let text, Icon, iconColor;

        switch (type) {
            case 'LOAN':
                text = 'Empréstimo';
                Icon = FaHandHoldingUsd;
                iconColor = 'text-blue-600';
                break;
            case 'RECEIVABLES_ASSIGNMENT':
                text = 'Cessão de recebíveis';
                Icon = FaFileExport;
                iconColor = 'text-green-600';
                break;
            case 'STRUCTURED_OPERATION':
                text = 'Operação estruturada';
                Icon = FaLayerGroup;
                iconColor = 'text-purple-600';
                break;
            case 'ACKNOWLEDGEMENT_OF_DEBT':
                text = 'Confissão de dívida';
                Icon = FaFileSignature;
                iconColor = 'text-orange-600';
                break;
            default:
                text = type || 'N/A';
                Icon = null;
                iconColor = 'text-gray-600';
        }

        return Icon ? (
            <Tooltip content={text}>
                <Icon className={`${iconColor} text-lg`} />
            </Tooltip>
        ) : text;
    };

    const formatWithRecourse = (withRecourse: boolean) => {
        const text = withRecourse ? 'Com regresso' : 'Sem regresso';
        const Icon = withRecourse ? FaLink : FaUnlink;
        const iconColor = withRecourse ? 'text-green-600' : 'text-red-600';

        return (
            <Tooltip content={text}>
                <Icon className={`${iconColor} text-lg`} />
            </Tooltip>
        );
    };

    const formatStatus = (status: string) => {
        return status === 'Finalizado com sucesso' ? 'Finalizado' : status;
    };

    const urlNavigate = (page: number, pageSize: number, field: string, order: string, search: string) => {
        return `/contracts/${page}/${pageSize}/${field}/${order}/s=${search};`;
    };

    const renderRows = (data: any) => {
        let contracts = data;

        if (data && data.list) {
            contracts = data.list;
        }

        if (!contracts || !Array.isArray(contracts)) {
            return [];
        }

        return contracts.map((item: any, index: number) => (
            <tr key={item.credit_operation_id || index} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} hover:bg-slate-300 hover:shadow-md border-b border border-gray-300 text-xs`}>
                <td className="py-2 px-2 whitespace-nowrap">{item.amendment_number > 0 ? `${item.sequencial_number}-${item.amendment_number}` : item.sequencial_number || 'N/A'}</td>
                <td className="py-2 px-2 whitespace-nowrap">
                    <BadgeStatus
                        color={getStatusColor(item.status)}
                        text={formatStatus(item.status) || 'N/A'}
                    />
                </td>
                <td className="py-2 px-2 whitespace-nowrap">{item.operation_number || 'N/A'}</td>
                <td className="py-2 px-2 whitespace-nowrap">
                    {item.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR' || p.participant_type_name === 'BORROWER')?.name || 'N/A'}
                </td>
                <td className="py-2 px-2 whitespace-nowrap">{formatOperationType(item.operation_type)}</td>
                <td className="py-2 px-2">{formatWithRecourse(item.with_recourse)}</td>
                <td className="py-2 px-2 whitespace-nowrap">{item.expiration_date ? item.expiration_date.split('T')[0].split('-').reverse().join('/') : 'N/A'}</td>
                <td className="py-2 px-2 whitespace-nowrap">{item.created_at ? item.created_at.split('T')[0].split('-').reverse().join('/') : 'N/A'}</td>
                <td className="py-2 px-2 whitespace-nowrap">{item.approved_on ? item.approved_on.split('T')[0].split('-').reverse().join('/') : 'N/A'}</td>
                <td className="py-2 px-2 whitespace-nowrap">
                    <BadgeStatus
                        color={item.billed ? 'green' : 'gray'}
                        text={item.billed ? 'Sim' : 'Não'}
                    />
                </td>
                <td className="py-2 px-2 whitespace-nowrap text-sm max-w-[120px]">
                    {item.billing_info ? (
                        <span
                            className="truncate block cursor-pointer text-blue-600 hover:underline"
                            onClick={() => setBillingInfoModal(item.billing_info)}
                        >
                            {item.billing_info}
                        </span>
                    ) : '-'}
                </td>
                <td className="py-2 px-2 text-center whitespace-nowrap">
                    <div className="flex gap-1 justify-center">
                    <button
                        onClick={() => {
                            switch (item.operation_type) {
                                case 'LOAN':
                                    navigate(`/operations/loan/${item.credit_operation_id || ''}`);
                                    break;
                                case 'RECEIVABLES_ASSIGNMENT':
                                    navigate(`/operations/receivables-assignment/${item.credit_operation_id || ''}`);
                                    break;
                                case 'STRUCTURED_OPERATION':
                                    navigate(`/operations/structured-operation/${item.credit_operation_id || ''}`);
                                    break;
                                case 'ACKNOWLEDGEMENT_OF_DEBT':
                                    navigate(`/operations/acknowledgement-debt/${item.credit_operation_id || ''}`);
                                    break;
                                default:
                                    navigate(`/operations/receivables-assignment/${item.credit_operation_id || ''}`);
                                    break;
                            }
                        }}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm whitespace-nowrap"
                    >
                        Gerenciar
                    </button>
                    <button
                        onClick={() => navigate(`/contracts/${item.credit_operation_id}/sign`)}
                        title="Enviar contrato para assinatura"
                        className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-sm whitespace-nowrap"
                    >
                        📄 Enviar
                    </button>
                    </div>
                </td>
            </tr>
        ));
    };

    const pageChanged = (newPage: number) => {
        navigate(urlNavigate(newPage - 1, Number.parseInt(pageSize), fieldSorted, orderSorted, searchFilter), { replace: true });
    };

    const pageSorted = (field: string, order: 'ASC' | 'DESC') => {
        navigate(urlNavigate(Number.parseInt(page), Number.parseInt(pageSize), field, order, searchFilter), { replace: true });
    };

    const pageSizeChanged = (newPageSize: number) => {
        navigate(urlNavigate(0, newPageSize, fieldSorted, orderSorted, searchFilter), { replace: true });
    };

    const onSearch = (value: string) => {
        navigate(urlNavigate(0, Number.parseInt(pageSize), fieldSorted, orderSorted, value), { replace: true });
    };

    /**
        * Reset filters and navigate to the first operations page, clearing the search query.
        * @example
        * onFilterClean()
        * // Navigates (void) e.g. to "/operations/0/100/id/ASC/s=;" and replaces the current history entry
        * @param {void} none - No parameters.
        * @returns {void} Nothing is returned; performs navigation as a side effect.
        * @description
        *   - Uses current pageSize, fieldSorted and orderSorted values from the component scope.
        *   - Clears the search/filter by passing an empty string to urlNavigate (resulting in s=;).
        *   - Calls navigate(..., { replace: true }) to replace the current history entry instead of pushing a new one.
        *   - Intended as a callback for a "clear filters" / "reset search" control in the UI.
        */
    const onFilterClean = () => {
        navigate(urlNavigate(0, Number.parseInt(pageSize), fieldSorted, orderSorted, ''), { replace: true });
    };

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    const tableHeaders = useMemo(() => [
        { id: 'sequencial_number', title: 'Código Contrato', canSort: true, field: 'sequencial_number' },
        { id: 'contract_built_status', title: 'Status', canSort: true, field: 'contract_built_status' },
        { id: 'operation_number', title: 'Código Operação', canSort: true, field: 'operation_number' },
        { id: 'assignor', title: 'Cedente', canSort: true, field: 'assignor' },
        { id: 'operation_type', title: 'Tipo Operação', canSort: true, field: 'operation_type' },
        { id: 'with_recourse', title: 'Regresso', canSort: true, field: 'with_recourse' },
        { id: 'due_date', title: 'Data Vencimento', canSort: true, field: 'due_date' },
        { id: 'created_at', title: 'Data Criação', canSort: true, field: 'created_at' },
        { id: 'approved_on', title: 'Data Aprovação', canSort: true, field: 'approved_on' },
        { id: 'billed', title: 'Faturado', canSort: true, field: 'billed' },
        { id: 'billing_info', title: 'Info Faturamento', canSort: false },
        { id: 'actions', title: 'Ações', canSort: false }
    ], []);

    return (
        <>
        <PageList
            pageTitle="Lista de Contratos"
            url={url}
            fetchHeaders={fetchHeaders}
            breadcrumb={[{ label: 'Contratos', url: "#" }]}
            tableHeaders={tableHeaders}
            defaultBackendHeader={false}
            tableFieldSort={fieldSorted}
            tableOrderSort={orderSorted}
            onTableSort={pageSorted}
            renderRows={renderRows}
            page={Number.parseInt(page) || 0}
            pageSize={Number.parseInt(pageSize) || 100}
            onPageChange={pageChanged}
            onItemsPerPageChange={pageSizeChanged}
            itemsLabel="contratos"
        >
            {{
                filterSlot: (
                    <div className="grid grid-cols-10 gap-4">
                        <div className="col-span-6"></div>
                        <div className="col-span-4">
                            <PageSearch
                                value={searchFilter}
                                label="Procurar contratos..."
                                onSearch={onSearch}
                                filterCleanVisible={true}
                                filterCleanEnabled={!!searchFilter}
                                configureVisible={false}
                                onFilterClean={onFilterClean}
                            />
                        </div>
                    </div>
                ),
                controlSlot: (
                    <div className='pt-4 relative'>
                        <button
                            type='button'
                            onClick={handleRefresh}
                            className='text-white bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2'
                            title="Recarregar tabela"
                        >
                            <FaSync />
                        </button>
                        <button
                            type='button'
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className='text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2'
                        >
                            <div className="flex items-center">
                                <FaPlus className='mr-2' />
                                <span>Nova Operação</span>
                                <FaChevronDown className='ml-2' />
                            </div>
                        </button>
                        {isDropdownOpen && (
                            <div className="absolute top-full right-0 mt-1 bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg z-50 min-w-max">
                                <Link
                                    to={`/contracts/new?operation_type=STRUCTURED_OPERATION`}
                                    onClick={() => setIsDropdownOpen(false)}
                                    className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-t whitespace-nowrap"
                                >
                                    CPRF estruturada
                                </Link>

                                <Link
                                    to={`/contracts/new?operation_type=RECEIVABLES_ASSIGNMENT`}
                                    onClick={() => setIsDropdownOpen(false)}
                                    className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600 whitespace-nowrap"
                                >
                                    Cessão de recebíveis
                                </Link>

                                <Link
                                    to={`/contracts/new?operation_type=ACKNOWLEDGEMENT_OF_DEBT`}
                                    onClick={() => setIsDropdownOpen(false)}
                                    className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-b whitespace-nowrap"
                                >
                                    Confissão de divídas
                                </Link>
                            </div>
                        )}
                    </div>
                )
            }}
        </PageList>
        {billingInfoModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setBillingInfoModal(null)}>
                <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                    <h3 className="text-lg font-semibold mb-4 dark:text-white">Informação de Faturamento</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{billingInfoModal}</p>
                    <div className="flex justify-end mt-4">
                        <button onClick={() => setBillingInfoModal(null)} className="px-4 py-2 bg-gray-200 dark:bg-slate-700 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-300">Fechar</button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default OperationsPage;